import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGlobals } from 'src/shared/contexts/appContext';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { AuthorityEnum } from 'src/shared/models/enums/authority.enum';
import { createUserTermsSignSession, getUserTermsStatus } from 'src/shared/services/userService';

// BankID-signeringen är konfigurerad i FS.Forms – samma hårdkodade bas som övriga forms-länkar i appen.
const FORMS_BASE_URL = 'https://forms.foreningssupport.se';

/**
 * Blockerar appen tills användaren godkänt villkoren med BankID (om kunden kräver det).
 * Failar öppet: om statuskollen inte går igenom släpps användaren in – gaten är en
 * användarupplevelse, inte en säkerhetsspärr.
 */
export const UserTermsGate = (props: PropsWithChildren<unknown>) => {
  const { children } = props;
  const { t } = useTranslation(['user', 'common']);
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { currentUser } = useGlobals();

  const [checked, setChecked] = useState<boolean>(false);
  const [blocked, setBlocked] = useState<boolean>(false);
  const [redirecting, setRedirecting] = useState<boolean>(false);

  // Undantag från villkorsgaten om användaren är Administratör/SuperAdmin på någon RIKTIG modul.
  // Backend lägger till syntetiska UI-produkter ("Inställningar"/"Organisation") med product_id = 0 och
  // authority = Administrator för alla med medlemsregister/flera föreningar. De får INTE räknas här – annars
  // skulle ingen icke-admin någonsin blockeras av villkoren. Filtrera därför på product_id > 0.
  const isAdmin = (currentUser?.sysProducts ?? []).some((x) => x.product_id > 0 && (x.authority ?? 0) >= AuthorityEnum.Administrator);

  useEffect(() => {
    if (isAdmin) {
      setChecked(true);
      return;
    }

    const checkStatus = async () => {
      try {
        const res = await getUserTermsStatus();
        if (isMounted()) setBlocked(res.data.required && !res.data.accepted);
      } catch (e) {
        // Failar öppet – en trasig statuskoll ska inte låsa ute användaren.
        ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setChecked(true);
      }
    };
    checkStatus();
  }, [isAdmin, isMounted]);

  const handleApprove = useCallback(async () => {
    setRedirecting(true);
    try {
      const res = await createUserTermsSignSession();
      const returnUrl = window.location.href;
      const url =
        `${FORMS_BASE_URL}/TermsApproval?token=${encodeURIComponent(res.data.token)}` +
        `&cId=${encodeURIComponent(currentUser?.custName ?? '')}` +
        `&returnUrl=${encodeURIComponent(returnUrl)}`;
      window.location.href = url;
    } catch (e) {
      ConsoleHelper.log(e);
      toast.error(t('user:terms.gateError') as string);
      if (isMounted()) setRedirecting(false);
    }
  }, [currentUser?.custName, isMounted, t]);

  if (!checked) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!blocked) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 520, width: '100%' }}>
        <Typography variant='h6' gutterBottom>
          {t('user:terms.gateTitle')}
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          {t('user:terms.gateDescription')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant='contained' disableElevation onClick={handleApprove} disabled={redirecting}>
            {t('user:terms.gateButton')}
          </Button>
          <Button variant='outlined' onClick={() => navigate(RoutesEnum.Logout)} disabled={redirecting}>
            {t('common:headerToolbar.logout')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};
